import { brand } from '@websrc/App/Helpers/brand'
import { useLocale } from '@websrc/App/Hooks/useLocale'
import { usePath } from '@websrc/App/Hooks/usePath'
import { buildOrganisationSchema } from '@websrc/Build/buildOrganisationSchema'
import { OtherLocaleUrl } from '@websrc/Build/Pages/getOtherLocaleUrls'
import { mapSEO } from '@websrc/DataMappers/mapSEO'
import { ensureTrailingSlash } from '@websrc/Util/ensureTrailingSlash'
import React from 'react'
import { Helmet } from 'react-helmet'
import { Product, WithContext } from 'schema-dts'

// Props passed specifically for SEO when building pages
export type SEOProps = ReturnType<typeof mapSEO> & {
  otherLocaleUrls?: OtherLocaleUrl[]
  productSchema?: WithContext<Product>
}

export const SEO = ({
  description,
  title,
  keywords,
  image,
  otherLocaleUrls,
  productSchema,
  disableIndex,
  canonical_url,
}: SEOProps) => {
  const currentLocale = useLocale()
  const path = usePath()
  const [lang, country] = currentLocale.split('-')
  const finalLocale = `${lang}_${country.toUpperCase()}`
  const baseURL = process.env.SITE_URL
  const canonicalHref = canonical_url ?? ensureTrailingSlash(`${baseURL}${path}`)
  const organisationSchema = buildOrganisationSchema(baseURL, currentLocale)

  return (
    <Helmet>
      <html lang={currentLocale} />

      <title>{title}</title>

      <link href={canonicalHref} rel={'canonical'} />

      <meta content={description} name={'description'} />
      <meta content={keywords} name={'keywords'} />
      {disableIndex && <meta content={'noindex,nofollow'} name={'robots'} />}
      <meta
        content={'width=device-width,initial-scale=1,shrink-to-fit=no,viewport-fit=cover'}
        name={'viewport'}
      />

      {/* OpenGraph tags */}
      <meta content={'website'} property={'og:type'} />
      <meta content={brand.name} property={'og:site_name'} />
      <meta content={finalLocale} property={'og:locale'} />
      <meta content={title} property={'og:title'} />
      <meta content={description} property={'og:description'} />
      {image && <meta content={image.url} property={'og:image'} />}

      {otherLocaleUrls?.map((localeUrl) => (
        <link
          href={String(baseURL) + localeUrl.url}
          hrefLang={localeUrl.locale}
          key={localeUrl.locale}
          rel={'alternate'}
        />
      ))}

      {productSchema && (
        <script id={'product-schema'} type={'application/ld+json'}>
          {JSON.stringify(productSchema)}
        </script>
      )}

      {currentLocale === 'en-us' && (
        <script id={'organization-schema'} type={'application/ld+json'}>
          {JSON.stringify(organisationSchema)}
        </script>
      )}
    </Helmet>
  )
}
